import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, Building2, FileText, Phone, Mail, MapPin, Info } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientRegistration = () => {
  const [step, setStep] = useState(1);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [clientId, setClientId] = useState('');
  
  const [formData, setFormData] = useState({
    // Company info
    company_name: '',
    establishment_type: 'nightclub',
    siret: '',
    tva_number: '',
    
    // Contact
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    
    // Account
    password: '',
    confirm_password: '',
    
    // Problem
    problem_description: '',
    establishment_capacity: '',
    sound_system_info: ''
  });

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateStep1 = () => {
    if (!formData.company_name || !formData.establishment_type) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.contact_person || !formData.email || !formData.phone || 
        !formData.address || !formData.postal_code || !formData.city) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Email invalide");
      return false;
    }
    
    return true;
  };

  const validateStep3 = () => {
    if (!formData.password || !formData.confirm_password) {
      toast.error("Veuillez définir un mot de passe");
      return false;
    }
    
    if (formData.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return false;
    }
    
    if (formData.password !== formData.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return false;
    }
    
    return true;
  };

  const validateStep4 = () => {
    if (!formData.problem_description) {
      toast.error("Veuillez décrire votre problématique");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    let isValid = false;
    
    if (step === 1) isValid = validateStep1();
    else if (step === 2) isValid = validateStep2();
    else if (step === 3) isValid = validateStep3();
    else if (step === 4) isValid = validateStep4();
    
    if (isValid) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep4()) return;

    try {
      const registrationData = {
        ...formData,
        establishment_capacity: formData.establishment_capacity ? parseInt(formData.establishment_capacity) : null
      };
      
      delete registrationData.confirm_password;

      const response = await axios.post(`${API}/clients/register`, registrationData);
      
      setClientId(response.data.client_id);
      setRegistrationComplete(true);
      toast.success("Inscription réussie !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
      console.error(error);
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardContent className="pt-8">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-700 mb-4">
                Inscription réussie !
              </h2>
              <p className="text-slate-600 mb-6">
                Votre demande d'inscription a été enregistrée avec succès.
                Vous recevrez un email de confirmation une fois votre compte validé par notre équipe.
              </p>
              
              <Alert className="mb-6 bg-blue-50 border-blue-200">
                <AlertDescription>
                  <div className="text-left space-y-2">
                    <p className="font-medium">Prochaines étapes :</p>
                    <ol className="list-decimal list-inside space-y-1 text-sm">
                      <li>Vérification de votre inscription par notre équipe (24-48h)</li>
                      <li>Réception d'un email d'activation</li>
                      <li>Connexion à votre espace client</li>
                      <li>Upload des documents administratifs nécessaires</li>
                      <li>Demande de devis personnalisé</li>
                    </ol>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <Button 
                  onClick={() => window.location.href = '/'}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  Retour à l'accueil
                </Button>
                <p className="text-sm text-slate-500">
                  Référence : {clientId.substring(0, 8)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-teal-800 mb-2">T-Kalé Pro-Acoustics</h1>
          <p className="text-slate-600">Inscription Établissements de Nuit</p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  s < step ? 'bg-teal-600 text-white' :
                  s === step ? 'bg-teal-600 text-white ring-4 ring-teal-200' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {s < step ? '✓' : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-teal-600' : 'bg-slate-200'}`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-600">
            <span>Entreprise</span>
            <span>Contact</span>
            <span>Compte</span>
            <span>Problématique</span>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && "Informations de l'Établissement"}
              {step === 2 && "Coordonnées"}
              {step === 3 && "Création du Compte"}
              {step === 4 && "Votre Problématique"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* STEP 1: Company Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Nom de l'établissement *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    placeholder="Ex: Le Groove Club"
                    required
                  />
                </div>

                <div>
                  <Label>Type d'établissement *</Label>
                  <Select 
                    value={formData.establishment_type} 
                    onValueChange={(val) => updateField('establishment_type', val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nightclub">🎵 Discothèque</SelectItem>
                      <SelectItem value="bar">🍺 Bar</SelectItem>
                      <SelectItem value="concert_hall">🎸 Salle de concert</SelectItem>
                      <SelectItem value="restaurant">🍽️ Restaurant</SelectItem>
                      <SelectItem value="lounge">🛋️ Lounge</SelectItem>
                      <SelectItem value="festival">🎪 Festival</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>N° SIRET</Label>
                    <Input
                      value={formData.siret}
                      onChange={(e) => updateField('siret', e.target.value)}
                      placeholder="123 456 789 00012"
                    />
                    <p className="text-xs text-slate-500 mt-1">14 chiffres</p>
                  </div>

                  <div>
                    <Label>N° TVA Intracommunautaire</Label>
                    <Input
                      value={formData.tva_number}
                      onChange={(e) => updateField('tva_number', e.target.value)}
                      placeholder="FR 12 345678901"
                    />
                  </div>
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-sm">
                    <Info className="h-4 w-4 inline mr-2" />
                    Les documents administratifs (KBIS, RIB, etc.) seront à fournir dans votre espace client après validation.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* STEP 2: Contact Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Personne de contact *</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => updateField('contact_person', e.target.value)}
                    placeholder="Prénom Nom"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="contact@exemple.fr"
                      required
                    />
                  </div>

                  <div>
                    <Label>Téléphone *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="06 12 34 56 78"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Adresse *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Rue de la Musique"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Code postal *</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      placeholder="75001"
                      required
                    />
                  </div>

                  <div>
                    <Label>Ville *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Paris"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Account Creation */}
            {step === 3 && (
              <div className="space-y-4">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertDescription>
                    <Info className="h-4 w-4 inline mr-2" />
                    Ces identifiants vous permettront d'accéder à votre espace client T-Kalé
                  </AlertDescription>
                </Alert>

                <div>
                  <Label>Email de connexion</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-slate-100"
                  />
                  <p className="text-xs text-slate-500 mt-1">Utilisé pour la connexion</p>
                </div>

                <div>
                  <Label>Mot de passe *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    placeholder="Minimum 8 caractères"
                    required
                  />
                </div>

                <div>
                  <Label>Confirmer le mot de passe *</Label>
                  <Input
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => updateField('confirm_password', e.target.value)}
                    placeholder="Retaper le mot de passe"
                    required
                  />
                </div>

                <div className="p-3 bg-slate-50 rounded text-sm">
                  <p className="font-medium mb-2">Sécurité du mot de passe :</p>
                  <ul className="space-y-1 text-slate-600">
                    <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                      {formData.password.length >= 8 ? '✓' : '○'} Au moins 8 caractères
                    </li>
                    <li className={formData.password === formData.confirm_password && formData.password ? 'text-green-600' : ''}>
                      {formData.password === formData.confirm_password && formData.password ? '✓' : '○'} Mots de passe identiques
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* STEP 4: Problem Description */}
            {step === 4 && (
              <div className="space-y-4">
                <div>
                  <Label>Description de votre problématique *</Label>
                  <Textarea
                    value={formData.problem_description}
                    onChange={(e) => updateField('problem_description', e.target.value)}
                    placeholder="Décrivez votre situation : nuisances sonores, problèmes de conformité réglementaire, plaintes de voisinage, besoin d'étude acoustique, etc."
                    rows={6}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Plus vous serez précis, mieux nous pourrons vous aider
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Capacité d'accueil</Label>
                    <Input
                      type="number"
                      value={formData.establishment_capacity}
                      onChange={(e) => updateField('establishment_capacity', e.target.value)}
                      placeholder="Ex: 300 personnes"
                    />
                  </div>

                  <div>
                    <Label>Informations système de sonorisation</Label>
                    <Input
                      value={formData.sound_system_info}
                      onChange={(e) => updateField('sound_system_info', e.target.value)}
                      placeholder="Marque, puissance, config..."
                    />
                  </div>
                </div>

                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription>
                    <CheckCircle className="h-4 w-4 inline mr-2 text-green-600" />
                    Après validation de votre compte, vous pourrez :
                    <ul className="mt-2 ml-6 list-disc text-sm space-y-1">
                      <li>Télécharger vos documents administratifs (KBIS, RIB, etc.)</li>
                      <li>Envoyer vos anciennes études acoustiques et plans</li>
                      <li>Demander un devis personnalisé</li>
                      <li>Accéder à votre tableau de bord</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                >
                  Précédent
                </Button>
              )}
              
              {step < 4 ? (
                <Button
                  onClick={handleNext}
                  className="ml-auto bg-teal-600 hover:bg-teal-700"
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="ml-auto bg-teal-600 hover:bg-teal-700"
                >
                  Finaliser l'inscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-600">
          <p>Déjà inscrit ? <a href="/" className="text-teal-600 hover:underline">Se connecter</a></p>
        </div>
      </div>
    </div>
  );
};

export default ClientRegistration;
